import type { FC } from 'react';
import { useEffect } from 'react';
import type { BaseFormOption } from 'src/components/form/form-option';
import { SearchableSingleSelect } from 'src/components/form/select/searchable-single-select';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type LookupFilterItem = {
  id: string;
  name: string;
};

type SearchFor =
  | 'external-trade-tso'
  | 'external-trade-networkpoint'
  | 'remit-report-tso'
  | 'remit-report-counterparty'
  | 'remit-report-networkpoint';
const searchForEndpoints: Record<SearchFor, string> = {
  'external-trade-tso': 'tsos-for-external-trade-filter',
  'external-trade-networkpoint': 'networkpoints-for-external-trades-filter',
  'remit-report-tso': 'tsos-for-secondary-remit-report-filter',
  'remit-report-counterparty': 'counterparties-for-remit-report-filter',
  'remit-report-networkpoint': 'networkpoints-for-remit-report-filter',
};

const useLookupSearch = (searchFor: SearchFor) =>
  useAxios(
    (axios, baseConfig) =>
      axios.request<LookupFilterItem[]>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.remitReporting}/api/platform/lookup/${searchForEndpoints[searchFor]}`,
      }),
    { neededOnPageLoad: false }
  );

export const SearchableSingleLookupSelect: FC<{
  name: string;
  label: string;
  searchFor: SearchFor;
}> = ({ name, label, searchFor }) => {
  const lookupSearch = useLookupSearch(searchFor);

  const items = lookupSearch.response?.data.map(
    (item) =>
      ({
        value: item.id,
        label: item.name,
      }) satisfies BaseFormOption<string>
  );

  const search = useClientSearch(items, (item, search) =>
    item.label.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || lookupSearch.response || lookupSearch.pending)
      return;
    lookupSearch.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, lookupSearch.response]);

  return (
    <SearchableSingleSelect name={name} label={label} {...search} stacked />
  );
};
